import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ChipGroup } from "@/components/ChipGroup";
import { api, errMsg } from "@/lib/api";
import { UserCheck, Loader2 } from "lucide-react";

export const DOCTORS = ["Dr. Afzal", "Dr. Lari", "Dr. Nazia"];
const today = () => new Date().toLocaleDateString("en-CA");
const EMPTY = { patient_name: "", visit_date: today(), patient_type: "NEW", mobile: "", aadhaar_number: "", ayushman_card_number: "", symptoms: "", attended_by: "" };
const inp = "h-11 rounded-2xl bg-white";
const L = ({ t, req, children }) => <label className="block space-y-1.5"><span className="text-xs font-medium text-neutral-500">{t}{req && <span className="text-rose-500"> *</span>}</span>{children}</label>;

export const VisitFormDialog = ({ open, onOpenChange, visit, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (!open) return;
    setMatch(null);
    setF(visit ? { ...EMPTY, ...visit, mobile: visit.mobile?.replace(/^\+91/, "") || "", aadhaar_number: visit.aadhaar_number || "", ayushman_card_number: visit.ayushman_card_number || "" } : { ...EMPTY, visit_date: today() });
  }, [open, visit]);

  const lookup = async () => {
    if (visit) return;
    const mobile = f.mobile.replace(/\D/g, "");
    if (mobile.length < 10) return;
    const { data } = await api.get("/clinic/lookup", { params: { mobile } });
    setMatch(data.found ? data : null);
    setF((x) => ({ ...x, patient_type: data.found ? "OLD" : "NEW", ...(data.found ? { patient_name: x.patient_name || data.patient_name, ayushman_card_number: x.ayushman_card_number || data.ayushman_card_number || "", aadhaar_number: x.aadhaar_number || data.aadhaar_number || "" } : {}) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.attended_by) return toast.error("Select the doctor who attended");
    setBusy(true);
    try {
      const body = { ...f, aadhaar_number: f.aadhaar_number || null, ayushman_card_number: f.ayushman_card_number || null };
      if (visit) await api.put(`/clinic/visits/${visit.id}`, body); else await api.post("/clinic/visits", body);
      toast.success(visit ? "Patient record updated" : "Patient registered");
      onOpenChange(false); onSaved();
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="visit-form-dialog" className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-xl">
        <DialogHeader><DialogTitle className="font-display">{visit ? "Edit patient visit" : "Register patient"}</DialogTitle><DialogDescription>Rahmat Clinic · patient register</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <L t="Mobile number" req><Input required data-testid="visit-mobile" inputMode="numeric" placeholder="10-digit mobile" value={f.mobile} onChange={(e) => { setMatch(null); setF((x) => ({ ...x, mobile: e.target.value, patient_type: "NEW" })); }} onBlur={lookup} className={inp} /></L>
          <L t="Aadhaar number"><Input data-testid="visit-aadhaar" inputMode="numeric" maxLength={14} placeholder="12 digits" value={f.aadhaar_number} onChange={set("aadhaar_number")} onBlur={lookup} className={inp} /></L>
          {match && (
            <p data-testid="visit-returning-banner" className="flex items-center gap-2 rounded-2xl bg-[#E6F5EF] px-4 py-3 text-sm text-[#137A5E] sm:col-span-2">
              <UserCheck className="h-4 w-4 shrink-0" />Returning patient: {match.patient_name} · {match.visits} earlier visit{match.visits > 1 ? "s" : ""} · last on {match.last_visit_date} with {match.last_attended_by}
            </p>
          )}
          <L t="Patient name" req><Input required data-testid="visit-name" placeholder="Full name" value={f.patient_name} onChange={set("patient_name")} className={inp} /></L>
          <L t="Date" req><Input required data-testid="visit-date" type="date" max={today()} value={f.visit_date} onChange={set("visit_date")} className={inp} /></L>
          <div className="sm:col-span-2"><L t="Patient type"><p data-testid="visit-patient-type" className={`rounded-2xl px-4 py-3 text-sm font-semibold ${f.patient_type === "OLD" ? "bg-neutral-100 text-neutral-700" : "bg-[#F5E04F] text-[#151515]"}`}>{f.patient_type === "OLD" ? "Old patient · mobile number found in clinic records" : "New patient · mobile number not found in clinic records"}</p></L></div>
          <div className="sm:col-span-2"><L t="Ayushman card number"><Input data-testid="visit-ayushman" placeholder="Optional" value={f.ayushman_card_number} onChange={set("ayushman_card_number")} className={inp} /></L></div>
          <div className="sm:col-span-2"><L t="Symptoms" req><Textarea required data-testid="visit-symptoms" rows={3} placeholder="Fever, cough, body ache…" value={f.symptoms} onChange={set("symptoms")} className="rounded-2xl bg-white" /></L></div>
          <div className="sm:col-span-2"><L t="Attended by" req><ChipGroup options={DOCTORS} value={f.attended_by} onChange={set("attended_by")} testPrefix="visit-doctor" /></L></div>
          <Button data-testid="visit-submit-btn" disabled={busy} className="h-12 rounded-full bg-[#1F9E7A] text-base hover:bg-[#178566] sm:col-span-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : visit ? "Save changes" : "Register patient"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
